import {
  CadModelCategoryFilterObjectData,
  CadModelCategoryFilterResponse,
  CreateProfileConfigurationRequest,
  ProfileConfigurationType,
  ProfileLevelType,
  UpdateProfileConfigurationRequest,
} from "@api/apiClient";
import { useCreateProfileConfiguration, usePredefinedProfileConfiguration, useProfileConfigurations, useUpdateProfileConfiguration } from "@api/profileConfigurations";
import { useProfileSearch } from "@api/profiles";
import i18n from "@i18n/i18n";
import { Box, Button, Card, CardContent, Container, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Snackbar, Typography } from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { DropdownOption } from "../UnMappedCadMaterialPage/UnMappedCadMaterialPage";
import CadModelCategoryFilterGrid, { ICadModelCategoryFilter } from "./CadModelCategoryFilterGrid";

const CadModelCategoryFilterPage = observer(() => {
  const [selectedProfileValue, setSelectedProfileValue] = useState<string>("");

  const defaultLabel = {
    label: i18n.t("cad-model-category-filter.profile.select-profile"),
    value: "",
  };

  const [profileOptions, setProfileOptions] = useState<DropdownOption[]>([defaultLabel]);

  const [selectedRows, setSelectedRows] = useState<ICadModelCategoryFilter[]>([]);

  const [profileConfiguration, setProfileConfiguration] = useState<CadModelCategoryFilterResponse | null>(null);
  const { data: profileData, isFetching: isProfileFetching, isLoading: isProfileLoading } = useProfileSearch([ProfileLevelType.Application], null);
  const {
    refetch: fetchProfileConfiguration,
    isLoading: isProfileConfigurationLoading,
    isFetching: isProfileConfigurationFetching,
  } = useProfileConfigurations(selectedProfileValue);

  const { data: predefinedProfileConfiguration } = usePredefinedProfileConfiguration();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const createProfileConfiguration = useCreateProfileConfiguration();
  const updateProfileConfiguration = useUpdateProfileConfiguration();

  const [toastMessage, setToastMessage] = useState<string>("");
  const [openSnackToast, setOpenSnackToast] = useState<boolean>(false);

  const handleProfileChange = (event: SelectChangeEvent) => {
    const { value } = event.target;
    setSelectedProfileValue(value as string);
  };

  useEffect(() => {
    if (profileData && profileData.length > 0) {
      const _profileOptions = profileData.map((profile) => ({
        label: profile.name,
        value: profile.profileId,
      }));
      const dropDownOptions = [{ ...defaultLabel }, ..._profileOptions];

      setProfileOptions(dropDownOptions as DropdownOption[]);
    }
  }, [profileData]);

  useEffect(() => {
    refetchProfileData();
  }, [selectedProfileValue]);

  const refetchProfileData = async () => {
    if (selectedProfileValue) {
      const refetchResult = await fetchProfileConfiguration();
      if (refetchResult.data) {
        setProfileConfiguration({ ...(refetchResult.data as any) });
      } else {
        const configurationObj = new CadModelCategoryFilterResponse();
        configurationObj.configurationObjectData = [];
        setProfileConfiguration(configurationObj);
      }
    }
  };

  const handlePredefinedProfileConfigurationSave = async () => {
    setProfileConfiguration((prevConfig) => {
      if (prevConfig) {
        const updatedConfig = new CadModelCategoryFilterResponse(prevConfig);
        updatedConfig.configurationObjectData = predefinedProfileConfiguration;
        return updatedConfig;
      }

      const newConfig = new CadModelCategoryFilterResponse();
      newConfig.configurationObjectData = predefinedProfileConfiguration;
      return newConfig;
    });
  };

  const handleSave = async () => {
    setIsSubmitting(true);

    // Map selected rows to configuration
    const _configuration = selectedRows.map((row) => ({
      categoryId: row.categoryId,
      category: row.name,
      categoryKey: row.key,
      categoryValue: row.value,
    })) as CadModelCategoryFilterObjectData[];

    const createProfileConfigurationRequest = {
      profileId: selectedProfileValue,
      configuration: _configuration,
      profileConfigurationType: ProfileConfigurationType.ModelElementFilter,
    } as CreateProfileConfigurationRequest;

    const updateProfileConfigurationRequest = {
      profileId: selectedProfileValue,
      configuration: _configuration,
      profileConfigurationType: ProfileConfigurationType.ModelElementFilter,
    } as UpdateProfileConfigurationRequest;

    try {
      if (profileConfiguration?.profileConfigurationId) {
        const data = {
          profileConfigurationId: profileConfiguration.profileConfigurationId,
          updateProfileConfigurationRequest,
        };

        const configuration = await updateProfileConfiguration.mutateAsync(data as any);
        console.log({ configuration });

        setToastMessage(i18n.t("cad-model-category-filter.notification.update"));
        setOpenSnackToast(true);
      } else {
        const configuration = await createProfileConfiguration.mutateAsync(createProfileConfigurationRequest as any);
        console.log({ configuration });

        setToastMessage(i18n.t("cad-model-category-filter.notification.create"));
        setOpenSnackToast(true);
      }

      refetchProfileData();
    } catch (error) {
      console.error("Error saving configuration:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ height: "100%" }}>
      <Grid mt={2} container spacing={2} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Grid size={12} sx={{ display: "flex", flexGrow: 0 }}>
          <Typography variant="h6" fontWeight="bold">
            {i18n.t("cad-model-category-filter.profile.title")}
          </Typography>
        </Grid>
        <Grid size={12} sx={{ display: "flex", flexGrow: 0, justifyContent: "start" }}>
          <Card elevation={3} sx={{ width: "100%", "&.MuiPaper-root": { transition: "none important!" } }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <InputLabel id="profile-label" sx={{ marginRight: 2 }}>
                  {i18n.t("cad-model-category-filter.profile.title")}
                </InputLabel>

                <Select labelId="profile-select-label" id="profile-select-label" value={selectedProfileValue} onChange={handleProfileChange} displayEmpty sx={{ width: "250px" }}>
                  {profileOptions.map((profile) => (
                    <MenuItem key={profile.value} value={profile.value}>
                      {profile.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button autoFocus variant="contained" type="submit" onClick={handlePredefinedProfileConfigurationSave} disabled={selectedProfileValue === ""}>
                  {i18n.t("cad-model-category-filter.btn-enable-pre-defined-categories")}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12} sx={{ display: "flex", flexGrow: 1, width: "100%", position: "relative" }}>
          <CadModelCategoryFilterGrid
            onSelectedRowsChange={setSelectedRows}
            selectedProfileConfiguration={profileConfiguration?.configurationObjectData}
            profileId={selectedProfileValue}
            loadProfileConfiguration={isProfileLoading || isProfileFetching || (selectedProfileValue ? isProfileConfigurationLoading || isProfileConfigurationFetching : false)}
          />
        </Grid>

        <Grid size={12} sx={{ display: "flex", flexGrow: 0, justifyContent: "end" }}>
          <Button autoFocus variant="contained" type="submit" onClick={handleSave} disabled={selectedProfileValue === ""} loading={isSubmitting}>
            {i18n.t("cad-model-category-filter.btn-save-profile-configuration")}
          </Button>
        </Grid>
      </Grid>

      {openSnackToast && (
        <Snackbar
          open={openSnackToast}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          message={toastMessage}
          onClose={() => setOpenSnackToast(false)}
        />
      )}
    </Container>
  );
});

export default CadModelCategoryFilterPage;
